import React, { useState, useRef } from 'react';
import { 
  Building2, 
  Briefcase, 
  LineChart, 
  Search, 
  ShieldCheck, 
  CheckCircle2, 
  ChevronRight, 
  ChevronLeft,
  Sliders, 
  UserCheck, 
  Zap, 
  Server,
  FileCheck,
  Landmark,
  Clock,
  Laptop,
  Lock,
  Globe
} from 'lucide-react';

const CopilotIcon = ({ size = 24, className = "" }) => (
  <img 
    src="https://en.wikipedia.org/wiki/Special:FilePath/Microsoft_365_Copilot.png" 
    alt="Copilot" 
    style={{ width: size, height: size }} 
    className={`object-contain ${className}`} 
    referrerPolicy="no-referrer" 
  />
);

const ClaudeIcon = ({ size = 24, className = "" }) => (
  <img 
    src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/58/Claude-ai-icon.svg/960px-Claude-ai-icon.svg.png" 
    alt="Claude" 
    style={{ width: size, height: size }} 
    className={`object-contain ${className}`} 
    referrerPolicy="no-referrer" 
  />
);

const ChatGPTIcon = ({ size = 24, className = "" }) => (
  <img 
    src="https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg" 
    alt="ChatGPT" 
    style={{ width: size, height: size }} 
    className={`object-contain ${className}`} 
    referrerPolicy="no-referrer" 
  />
);

const GeminiIcon = ({ size = 24, className = "" }) => (
  <img 
    src="https://upload.wikimedia.org/wikipedia/commons/1/1d/Google_Gemini_icon_2025.svg" 
    alt="Gemini" 
    style={{ width: size, height: size }} 
    className={`object-contain ${className}`} 
    referrerPolicy="no-referrer" 
  />
);

const verticals = [
  {
    id: "pe_vc",
    title: "Private Equity & VC",
    icon: Briefcase,
    color: "emerald",
    desc: "Target sourcing, portfolio monitoring, EBITDA variance tracking, and Investment Committee memos.",
    outcomes: [
      "Real-time financial audits comparing monthly portfolio performance against underwriting models",
      "Automated sourcing pipeline for unlisted companies and personalized founder outreach",
      "100-day value creation roadmap tracking and pricing optimization"
    ]
  },
  {
    id: "ib_ma",
    title: "Investment Banking & M&A",
    icon: Building2,
    color: "blue",
    desc: "CIM & teaser generation, process letters, buyer mapping, and merger modeling.",
    outcomes: [
      "Accelerating 500-page VDR synthesis into buyer-ready teasers",
      "Automated buyer universe matching based on historic M&A criteria",
      "Verifiable citation back-links for every transaction parameter"
    ]
  },
  {
    id: "fin_analysis",
    title: "Financial Analysis & DCF",
    icon: LineChart,
    color: "purple",
    desc: "Trading comps, DCF/LBO logic stress testing, and Excel model audits.",
    outcomes: [
      "Automated logic stress tests on complex financial models",
      "3-statement link integrity checks and formula error flags",
      "Audit-ready model summaries for Investment Committee review"
    ]
  },
  {
    id: "equity_res",
    title: "Equity Research & Public Markets",
    icon: Search,
    color: "amber",
    desc: "Earnings previews/reviews, catalyst tracking, and live market data feeds.",
    outcomes: [
      "Connecting AI reasoning directly to live financial feeds (LSEG / S&P)",
      "Automated earnings preview/review notes and catalyst tracking",
      "Macro-rates and options volatility monitoring"
    ]
  },
  {
    id: "wealth_mgmt",
    title: "Private Wealth & Family Offices",
    icon: Landmark,
    color: "rose",
    desc: "Client QBRs, portfolio rebalancing commentary, and Tax-Loss Harvesting (TLH) logic.",
    outcomes: [
      "Automated drafting of personalized quarterly client performance reviews",
      "Synthesizing estate planning & Tax-Loss Harvesting rules grids",
      "Generating customized portfolio rebalancing rationale and reporting"
    ]
  },
  {
    id: "fund_ops",
    title: "Fund Admin & Compliance",
    icon: ShieldCheck,
    color: "cyan",
    desc: "General Ledger reconciliation, break tracing, NAV tie-outs, and KYC parsing.",
    outcomes: [
      "Reducing monthly NAV reconciliation cycles from days to minutes",
      "Automated General Ledger break-tracing and accrual commentary",
      "Automated regulatory filing checks and compliance review for SEC & Reg S-P guidelines"
    ]
  }
];

const platforms = [
  {
    id: "copilot",
    name: "Microsoft Copilot",
    tag: "M365 Enterprise",
    icon: CopilotIcon,
    desc: "Custom Copilot Studio agents operating inside Word, Excel, Teams under enterprise security perimeters."
  },
  {
    id: "claude",
    name: "Claude CoWork",
    tag: "Deep Reasoning",
    icon: ClaudeIcon,
    desc: "Specialized financial workflows with verifiable source-level citations for every output."
  },
  {
    id: "chatgpt",
    name: "ChatGPT Ent.",
    tag: "Firm-Wide Canvas",
    icon: ChatGPTIcon,
    desc: "Custom GPTs and Canvas collaborative workspaces for standardized memo drafting and research."
  },
  {
    id: "gemini",
    name: "Google Gemini",
    tag: "Massive Context",
    icon: GeminiIcon,
    desc: "Built for massive document analysis—instantly cross-referencing thousands of pages of virtual data rooms, transcripts, and market feeds."
  },
  {
    id: "sovereign",
    name: "Sovereign Vaults",
    tag: "Zero Data Leak",
    icon: Server,
    desc: "Private, zero-leakage infrastructure that connects top AI engines directly to your firm's internal files and deal history."
  }
];

const featuredWebcasts = [
  {
    id: "sovereign_brain_flagship",
    badge: "Flagship Executive Module",
    badgeColor: "bg-gradient-to-r from-emerald-500/20 to-blue-500/20 text-emerald-400 border-emerald-500/30",
    trackBadge: "🌐 5x Delivered Globally",
    trackBadgeColor: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    title: "Smart Workflows with Claude CoWork",
    subtitle: "Live Demo of Model Context in Action",
    targetAudience: "C-Suite, Managing Directors, and Strategy Leads addressing 'Shadow AI' risks.",
    vertical: "Cross-Vertical (M&A, PE, Strategy)",
    platform: "Claude CoWork & Sovereign Vaults",
    deliverable: "Automated IC Memo Generation & Strategy Playbook"
  },
  {
    id: "pe_surveillance",
    badge: "PE & VC Flagship",
    badgeColor: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    trackBadge: "⚡ 1x Delivered (APAC)",
    trackBadgeColor: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    title: "AI Workflows for Continuous Deal Monitoring",
    subtitle: "Automated 24/7 Tracking for Portfolio Health & Deal Pipelines",
    targetAudience: "PE Partners, Operating Partners, and Value Creation Leads managing portfolio margins.",
    vertical: "Private Equity & Venture Capital",
    platform: "Claude CoWork / Native Skills",
    deliverable: "PortCo Variance Audit Report & Value Creation Playbook"
  },
  {
    id: "copilot_focus",
    badge: "Most Requested by Clients",
    badgeColor: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    trackBadge: "🗓️ Scheduled (APAC)",
    trackBadgeColor: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    title: "Unlocking Microsoft Copilot for Dealmakers",
    subtitle: "Advanced M&A, PE & Portfolio Workflows in M365",
    targetAudience: "Firms with active M365 Copilot licenses seeking high-value dealmaking output.",
    vertical: "PE, M&A & Corporate Strategy",
    platform: "Microsoft Copilot & Copilot Studio",
    deliverable: "Copilot Studio Prompt Templates & Excel Audit Workbooks"
  },
  {
    id: "ib_automation",
    badge: "M&A Advisory High ROI",
    badgeColor: "bg-purple-500/10 text-purple-400 border-purple-500/30",
    trackBadge: "✨ Fresh Release",
    trackBadgeColor: "bg-purple-500/10 text-purple-400 border-purple-500/30",
    title: "AI Workflows in Investment Banking",
    subtitle: "Accelerating CIMs, Teasers, Buyer Lists & Merger Models",
    targetAudience: "Investment Banking MDs, M&A Advisory Teams & Strategy Leads.",
    vertical: "Investment Banking & Advisory",
    platform: "ChatGPT Enterprise / Multi-Model",
    deliverable: "Automated VDR Review Tools & CIM Drafting Playbook"
  }
];

const speakersData = {
  balbinder: {
    id: "balbinder",
    name: "Balbinder Singh Bhatia",
    title: "Partner, Frontier | Strategy & Agents",
    role: "PE & Investment Banking Lead",
    location: "Abu Dhabi",
    image: "https://res.cloudinary.com/rp2zwlm2/image/upload/v1784102433/profile_balbinder_a4tvtn.png",
    background: "Ex-Accenture Strategy (PE & M&A Advisory), Espalier AI.",
    description: "Over 20 years of experience advising global funds on deal strategy, M&A execution, and practical enterprise AI deployment."
  },
  maheshwar: {
    id: "maheshwar",
    name: "Maheshwar Damojipurapu",
    title: "Partner, Frontier | Strategy & Agents",
    role: "Corporate Strategy, M&A & AI Adoption",
    location: "Bengaluru",
    image: "https://res.cloudinary.com/rp2zwlm2/image/upload/v1784102433/profile_maheshwar_etxduh.png",
    background: "Former Head of Innovation at Citrix & EdgeVerve.",
    description: "20+ years in corporate strategy, M&A, and AI. Advises global enterprises on architecting production-grade, sovereign AI infrastructure and agentic automation."
  }
};

export default function MasterclassCatalogue() {
  const [selectedVertical, setSelectedVertical] = useState("pe_vc");
  const [selectedPlatform, setSelectedPlatform] = useState("copilot");
  const [activeTab, setActiveTab] = useState("builder");
  const [selectedSpeaker, setSelectedSpeaker] = useState("balbinder");
  const [isLightMode, setIsLightMode] = useState(false);
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const { current } = scrollRef;
      const scrollAmount = direction === 'left' ? -500 : 500;
      current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const currentVertObj = verticals.find(v => v.id === selectedVertical) || verticals[0];
  const currentPlatObj = platforms.find(p => p.id === selectedPlatform) || platforms[0];
  const currentSpeakerObj = speakersData[selectedSpeaker];

  const themeClass = isLightMode 
    ? "bg-slate-50 text-slate-900 border-slate-200" 
    : "bg-slate-950 text-slate-100 border-slate-800";

  return (
    <div className={`min-h-screen p-4 md:p-8 font-sans transition-all duration-300 ${themeClass}`}>
      <div className="max-w-[1400px] mx-auto space-y-6">
        
        {/* Header Block */}
        <div className={`p-6 md:p-8 rounded-3xl border shadow-2xl flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 ${
          isLightMode ? 'bg-white border-slate-200' : 'bg-slate-900/90 border-slate-800'
        }`}>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-black tracking-widest uppercase rounded-full">
                GLG Masterclass Catalogue
              </span>
              <span className="text-slate-500 text-xs">•</span>
              <span className="text-slate-400 text-xs font-semibold">Financial Services Curriculum</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <span className="bg-gradient-to-r from-emerald-400 via-blue-400 to-purple-500 bg-clip-text text-transparent">AI Conviction:</span> 
              <span>Smart Workflows for Executives</span>
            </h1>
            <p className="text-slate-400 text-sm mt-2 max-w-2xl">
              Tailored executive briefings matching <strong className="text-slate-300 font-bold">specific financial domain</strong> roles with your firm's <strong className="text-slate-300 font-bold">approved AI tools</strong>.
            </p>
          </div>
        </div>

        {/* Custom Tab Navigation */}
        <div className="flex bg-slate-900/80 p-1.5 rounded-2xl border border-slate-800 max-w-fit overflow-x-auto">
          <button
            onClick={() => setActiveTab("builder")}
            className={`py-2.5 px-5 rounded-xl flex items-center gap-2 text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap ${
              activeTab === "builder"
                ? "bg-slate-800 text-white border border-slate-700 shadow-lg"
                : "text-slate-400 hover:text-slate-200 hover:bg-slate-900"
            }`}
          >
            <Sliders size={15} />
            Session Builder
          </button>

          <button
            onClick={() => setActiveTab("featured")}
            className={`py-2.5 px-5 rounded-xl flex items-center gap-2 text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap ${
              activeTab === "featured"
                ? "bg-slate-800 text-white border border-slate-700 shadow-lg"
                : "text-slate-400 hover:text-slate-200 hover:bg-slate-900"
            }`}
          >
            <Zap size={15} />
            Ready-to-Host Webcasts
            <span className="ml-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-emerald-500 to-blue-500 text-white text-[9px] font-black uppercase tracking-widest shadow-sm">
              Popular
            </span>
          </button>

          <button
            onClick={() => setActiveTab("speaker")}
            className={`py-2.5 px-5 rounded-xl flex items-center gap-2 text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap ${
              activeTab === "speaker"
                ? "bg-slate-800 text-white border border-slate-700 shadow-lg"
                : "text-slate-400 hover:text-slate-200 hover:bg-slate-900"
            }`}
          >
            <UserCheck size={15} />
            Expert Faculty & Formats
          </button>
        </div>

        {/* TAB 1: Builder */}
        {activeTab === "builder" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
            
            {/* LEFT COLUMN: Selectors (7 columns) */}
            <div className="lg:col-span-7 space-y-6">
              
              {/* Subtle Tech Stack Chips */}
              <div className={`p-4 rounded-3xl border ${isLightMode ? 'bg-white border-slate-200' : 'bg-slate-900/40 border-slate-800'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] font-mono uppercase text-slate-400 font-bold tracking-wider">Approved AI Platform</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {platforms.map(p => (
                    <button
                      key={p.id}
                      onClick={() => setSelectedPlatform(p.id)}
                      className={`py-1.5 px-3 rounded-full border text-xs font-semibold flex items-center gap-2 transition-all ${
                        selectedPlatform === p.id
                          ? "bg-emerald-500/15 border-emerald-500/80 text-emerald-400 shadow-sm"
                          : "bg-slate-950/60 border-slate-800/80 text-slate-400 hover:border-slate-700 hover:text-slate-200"
                      }`}
                    >
                      <p.icon size={14} />
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Prominent Financial Verticals list */}
              <div className={`p-4 rounded-3xl border ${isLightMode ? 'bg-white border-slate-200' : 'bg-slate-900/40 border-slate-800'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] font-mono uppercase text-emerald-400 font-bold tracking-wider">Select Domain Focus</span>
                </div>
                <div className="space-y-2.5">
                  {verticals.map(v => (
                    <button
                      key={v.id}
                      onClick={() => setSelectedVertical(v.id)}
                      className={`w-full p-4 rounded-2xl border text-left transition-all flex items-center justify-between group ${
                        selectedVertical === v.id
                          ? "bg-slate-800/80 border-slate-600 text-white shadow-lg"
                          : "bg-slate-950/60 border-slate-800/80 text-slate-400 hover:border-slate-700 hover:text-slate-200"
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2.5 rounded-xl transition-colors ${selectedVertical === v.id ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800 text-slate-400 group-hover:text-slate-200 group-hover:bg-slate-700'}`}>
                          <v.icon size={20} />
                        </div>
                        <div>
                          <div className={`font-bold text-sm ${selectedVertical === v.id ? 'text-white' : 'text-slate-300'}`}>{v.title}</div>
                          <div className="text-xs text-slate-500 line-clamp-1 mt-0.5 pr-2">{v.desc}</div>
                        </div>
                      </div>
                      <ChevronRight size={18} className={selectedVertical === v.id ? 'text-emerald-400 shrink-0' : 'text-slate-600 shrink-0 group-hover:text-slate-400'} />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: Output Profile (5 columns) */}
            <div className="lg:col-span-5">
              <div className="sticky top-6 rounded-3xl border border-emerald-500/30 bg-emerald-950/10 shadow-[0_0_40px_rgba(16,185,129,0.05)] overflow-hidden flex flex-col">
                
                {/* Embedded Speaker Header */}
                <div className="px-6 py-5 border-b border-emerald-500/20 bg-slate-900/50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-emerald-500/30 shrink-0">
                      <img 
                        src={currentSpeakerObj.image}
                        alt={currentSpeakerObj.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-white">{currentSpeakerObj.name}</h4>
                      <div className="flex flex-wrap items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-slate-400">{currentSpeakerObj.title}</span>
                        <span className="text-[9px] bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded border border-slate-700">{currentSpeakerObj.role}</span>
                        <span className="text-[9px] flex items-center gap-1 bg-slate-800/50 text-slate-400 px-1.5 py-0.5 rounded border border-slate-700/50">
                          <Globe size={10} /> {currentSpeakerObj.location}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="shrink-0 flex items-center gap-1.5 bg-slate-950/60 p-1 rounded-xl border border-slate-800/80">
                    {Object.values(speakersData).map((spk) => (
                      <button
                        key={spk.id}
                        onClick={() => setSelectedSpeaker(spk.id)}
                        className={`w-8 h-8 rounded-lg overflow-hidden border-2 transition-all ${
                          selectedSpeaker === spk.id
                            ? "border-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.4)] opacity-100"
                            : "border-transparent opacity-40 hover:opacity-100"
                        }`}
                        title={`Select ${spk.name} as Lead`}
                      >
                        <img src={spk.image} alt={spk.name} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Session Details Content */}
                <div className="p-6 md:p-8 flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[10px] font-mono uppercase text-emerald-400 font-bold tracking-wider bg-emerald-950/50 px-2 py-0.5 rounded border border-emerald-900">Custom GLG Module</span>
                  </div>
                  
                  <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-2 tracking-tight leading-tight">
                    {currentVertObj.title}
                  </h3>
                  <div className="flex items-center gap-2.5 text-slate-300 font-medium text-sm mb-8 pb-6 border-b border-slate-800">
                    Powered natively by
                    <span className="flex items-center gap-1.5 bg-slate-900 px-2.5 py-1 rounded-lg border border-slate-700 text-white font-bold text-sm">
                      <currentPlatObj.icon size={16} />
                      {currentPlatObj.name}
                    </span>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h4 className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-3">Key Session Outcomes:</h4>
                      <div className="space-y-2.5">
                        {currentVertObj.outcomes.map((outcome, idx) => (
                          <div key={idx} className="flex items-start gap-3 text-sm text-slate-200">
                            <CheckCircle2 size={18} className="text-emerald-400 shrink-0 mt-0.5" />
                            <span className="leading-relaxed">{outcome}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-slate-900/80 p-5 rounded-2xl border border-slate-800">
                      <h4 className="text-[10px] font-bold uppercase text-slate-500 tracking-wider mb-2">Technical Context</h4>
                      <p className="text-xs text-slate-300 leading-relaxed mb-4">{currentPlatObj.desc}</p>
                      <div className="pt-3 border-t border-slate-800 flex items-center gap-3">
                        <div className="p-2 bg-emerald-500/10 rounded-lg text-emerald-400">
                          <FileCheck size={16} />
                        </div>
                        <div>
                          <span className="text-[10px] uppercase font-mono text-slate-500 block leading-none mb-1">Session Deliverable</span>
                          <span className="text-xs font-bold text-emerald-400">Standard Operating Prompts & Playbook</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* TAB 2: Featured Webcasts (Horizontal Scroll) */}
        {activeTab === "featured" && (
          <div className="relative group w-full">
            {/* Left Scroll Arrow */}
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 -ml-5 z-10 p-3 rounded-full bg-slate-800 text-white shadow-xl opacity-0 group-hover:opacity-100 transition-all border border-slate-700 hover:bg-slate-700 hover:scale-110"
            >
              <ChevronLeft size={24} />
            </button>

            {/* Scroll Container */}
            <div ref={scrollRef} className="flex overflow-x-auto snap-x snap-mandatory gap-6 pb-6 pt-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] scroll-smooth px-2">
              {featuredWebcasts.map((w) => (
                <div key={w.id} className="shrink-0 w-[85vw] sm:w-[480px] snap-center p-8 min-h-[460px] rounded-3xl border border-slate-800 bg-slate-900/40 shadow-xl flex flex-col justify-between hover:border-slate-700 hover:shadow-2xl transition-all duration-300">
                  <div>
                    <div className="flex justify-between items-start gap-3 mb-5">
                      <span className={`px-3 py-1.5 border text-xs font-black uppercase tracking-wider rounded-full inline-block ${w.badgeColor}`}>
                        {w.badge}
                      </span>
                      <span className={`px-2.5 py-1.5 border text-[10px] font-black uppercase tracking-wider rounded-full inline-flex items-center text-center shrink-0 shadow-sm ${w.trackBadgeColor}`}>
                        {w.trackBadge}
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2 leading-tight">{w.title}</h3>
                    <p className="text-sm text-emerald-400 font-semibold mb-5">{w.subtitle}</p>
                    <p className="text-sm text-slate-400 leading-relaxed mb-8">{w.targetAudience}</p>
                    
                    <div className="space-y-3 mb-8 text-sm border-t border-slate-800/80 pt-5">
                      <div className="flex justify-between items-start flex-col gap-1">
                        <span className="text-slate-500 text-xs">Vertical:</span>
                        <span className="text-slate-200 font-medium">{w.vertical}</span>
                      </div>
                      <div className="flex justify-between items-start flex-col gap-1 mt-3">
                        <span className="text-slate-500 text-xs">Platform:</span>
                        <span className="text-emerald-400 font-medium flex items-center gap-1.5">
                          {w.platform.includes('Copilot') && <CopilotIcon size={16} />}
                          {w.platform.includes('Claude') && <ClaudeIcon size={16} />}
                          {w.platform.includes('ChatGPT') && <ChatGPTIcon size={16} />}
                          {w.platform.includes('Gemini') && <GeminiIcon size={16} />}
                          {w.platform}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-5 bg-slate-950/80 rounded-xl border border-slate-800 text-sm mt-auto">
                    <span className="text-xs uppercase font-mono text-slate-500 block mb-1.5">Session Deliverable:</span>
                    <span className="font-bold text-slate-200">{w.deliverable}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Right Scroll Arrow */}
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 -mr-5 z-10 p-3 rounded-full bg-slate-800 text-white shadow-xl opacity-0 group-hover:opacity-100 transition-all border border-slate-700 hover:bg-slate-700 hover:scale-110"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        )}

        {/* TAB 3: Speakers & Formats */}
        {activeTab === "speaker" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 flex flex-col space-y-6">
              <h3 className="text-3xl font-extrabold text-white">Expert Faculty</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 h-full">
                {Object.values(speakersData).map((spk) => (
                  <div key={spk.id} className={`p-8 rounded-3xl border flex flex-col items-center text-center transition-all duration-300 ${
                    selectedSpeaker === spk.id ? 'border-emerald-500/50 bg-slate-900/60 shadow-[0_0_30px_rgba(16,185,129,0.05)]' : 'border-slate-800 bg-slate-900/30 hover:border-slate-700'
                  }`}>
                    <div className={`w-32 h-32 rounded-full overflow-hidden border-4 mb-5 shadow-xl transition-all ${
                      selectedSpeaker === spk.id ? 'border-emerald-500/50' : 'border-slate-800'
                    }`}>
                      <img 
                        src={spk.image} 
                        alt={spk.name} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-xl font-extrabold text-white leading-tight">{spk.name}</h3>
                    <p className="text-xs text-emerald-400 font-bold uppercase tracking-wider mt-1.5">{spk.role}</p>
                    
                    <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400 mt-2 font-medium">
                      <Globe size={12} className="text-slate-500" /> Based in {spk.location}
                    </div>

                    <p className="text-xs text-slate-400 mt-3 font-medium bg-slate-950 px-3 py-1 rounded-full border border-slate-800 inline-block">{spk.background}</p>
                    <p className="text-sm text-slate-400 mt-5 leading-relaxed flex-1">
                      {spk.description}
                    </p>
                    
                    <button 
                      onClick={() => {
                        setSelectedSpeaker(spk.id);
                        setActiveTab("builder");
                      }}
                      className={`mt-6 w-full py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all border flex justify-center items-center gap-2 ${
                        selectedSpeaker === spk.id 
                          ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' 
                          : 'bg-slate-950 border-slate-700 text-slate-300 hover:bg-slate-800'
                      }`}
                    >
                      {selectedSpeaker === spk.id ? <><CheckCircle2 size={16} /> Selected Lead</> : 'Select as Lead'}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 p-8 rounded-3xl border border-slate-800 bg-slate-900/40 flex flex-col justify-center space-y-6">
              <h3 className="text-2xl font-extrabold text-white mb-2">Flexible Masterclass Formats</h3>
              <div className="space-y-4">
                {[
                  { title: "60-Minute Executive Briefing", desc: "Fast-paced executive briefing featuring strategic deal workflows, real-world session outputs, and interactive Q&A.", icon: Clock },
                  { title: "90-Minute Interactive Workshop", desc: "Includes live workflow exercises, prompt engineering frameworks, and platform comparisons.", icon: Laptop },
                  { title: "Private Roundtable / Closed Room", desc: "Closed-door sessions for single firms focusing heavily on Private Agents, SEC compliance, and zero-retention IP protection.", icon: Lock }
                ].map((f, i) => (
                  <div key={i} className="p-5 rounded-2xl bg-slate-950/60 border border-slate-800 transition-all hover:border-slate-700 hover:bg-slate-900/60 flex items-start gap-4">
                    <div className="p-3 bg-emerald-500/10 text-emerald-400 rounded-xl border border-emerald-500/20 shrink-0 mt-0.5">
                      <f.icon size={20} />
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-emerald-300">{f.title}</h4>
                      <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="p-4 border-t border-slate-800/80 flex flex-col md:flex-row justify-between items-center text-xs text-slate-500 gap-2 mt-8">
          <span>GLG Executive Masterclass Series • Frontier | Strategy & Agents</span>
          <span>Contact for Custom Regional Webcasts</span>
        </div>

      </div>
    </div>
  );
}